import { Text, XStack, YStack } from 'tamagui'

import { useApp, type Localized } from '../../../i18n'
import { Avatar, FormInput, Icon, IconButton, PressCard } from '../../../ui'
import type { Profile } from '../../../lib/types'
import { useUserSearch } from '../../users/useUserSearch'
import { Field } from './shared'

/**
 * Выбор одного сотрудника из справочника: поиск с 2 символов (тот же пикер, что у маршрута
 * произвольного документа), до 8 совпадений, выбранный показывается строкой с крестиком.
 * `excludeIds` — кого нет в выдаче (сам пользователь и т.п.); уже выбранный скрыт и так.
 * `hint` рисуется под полем, когда задан. Общий кусок «Подать от имени» и «Замещающего».
 * `value` — только то, что рисуется строкой: у замещающего из карточки заявки есть лишь id и ФИО.
 */
export function PersonPickField({
  label,
  hint,
  value,
  onChange,
  excludeIds,
}: {
  label: Localized
  hint?: Localized
  value: Pick<Profile, 'id' | 'fullName' | 'jobTitle' | 'avatarUrl'> | null
  onChange: (p: Profile | null) => void
  excludeIds: string[]
}) {
  const { t } = useApp()
  const { q, setQ, hits } = useUserSearch()

  const found = (hits ?? []).filter((p) => !excludeIds.includes(p.id) && p.id !== value?.id).slice(0, 8)

  return (
    <Field label={t(label)}>
      <YStack gap={8}>
        {value ? (
          <XStack alignItems="center" gap={10}>
            <Avatar name={value.fullName} uri={value.avatarUrl ?? undefined} size={28} />
            <YStack flex={1}>
              <Text fontSize={13} color="$color">{value.fullName}</Text>
              {!!value.jobTitle && (
                <Text fontSize={11} color="$muted">{value.jobTitle}</Text>
              )}
            </YStack>
            <IconButton
              aria-label={`${t({ ru: 'Убрать', en: 'Remove', az: 'Sil' })} ${value.fullName}`}
              onPress={() => onChange(null)}
            >
              <Icon name="x" size={15} color="$muted" />
            </IconButton>
          </XStack>
        ) : (
          <>
            <FormInput
              value={q}
              onChangeText={setQ}
              placeholder={t({ ru: 'Поиск сотрудника (от 2 символов)', en: 'Search employee (2+ chars)' })}
            />
            {found.map((p) => (
              <PressCard
                key={p.id}
                padding={9}
                onPress={() => {
                  onChange(p)
                  setQ('')
                }}
              >
                <XStack alignItems="center" gap={10}>
                  <Avatar name={p.fullName} uri={p.avatarUrl ?? undefined} size={28} />
                  <YStack flex={1}>
                    <Text fontSize={13} color="$color">{p.fullName}</Text>
                    {!!p.jobTitle && (
                      <Text fontSize={11} color="$muted">{p.jobTitle}</Text>
                    )}
                  </YStack>
                  <Icon name="plus" size={15} color="$primary" />
                </XStack>
              </PressCard>
            ))}
          </>
        )}
        {hint ? <Text fontSize={11} color="$muted">{t(hint)}</Text> : null}
      </YStack>
    </Field>
  )
}
